import React from 'react';
import _ from 'lodash';

import {
  ApprovalTypeName,
  QueryReturnAuthorizationsFilterInput,
  ReturnAuthorizationsActivity_ReturnAuthorizationTableQueryVariables,
  ReturnAuthorizationsActivity_TabBarDocument,
  useGenerateReturnLabelMutation,
  useReturnAuthorizationsActivity_ReturnAuthorizationTableLazyQuery
} from 'generated/graphql';
import {
  errorUtil,
  listQueryUtil
} from 'iron-forge-react-modules';

import { NetSuiteRecordChip } from 'modules';
import * as ShippingStatusChip from './ShippingStatusChip';
import { DateTime } from 'luxon';
import Big from 'big.js';
import * as types from './types';



const useListQuery = listQueryUtil.genUseListQuery<types.IColumnId>();



export interface IReturnAuthorization {
  id: string;
  returnDate: DateTime;
  returnNumber: string;
  order: {
    id: string;
    orderDate: DateTime;
    orderNumber: string;
    totalAmountCents: Big;
    externalId: string;
    urlToExternalOrder: string;
    netSuiteWriteStatus: NetSuiteRecordChip.IWriteStatus;
  };
  customer: {
    id: string;
    fullName: string;
    phoneNumber: string;
    email: string;
  };
  store: {
    id: string;
    name: string;
  };

  shipment: {
    id: string;
    labelUrl: string;
    status: ShippingStatusChip.IShippingStatus;
  } | null;
  approval: {
    createdDate: DateTime;
    approvalStatus: ApprovalTypeName;
  } | null;
  acceptance: {
    createdDate: DateTime;
    approvalStatus: ApprovalTypeName;
  } | null;
  itemsToBeReturnedCount: number;
  itemsFulfilledCount: number;
  itemsReceivedCount: number;
  returnAuthorizationNetSuiteWriteStatus: NetSuiteRecordChip.IWriteStatus;
}

export interface IData {
  returnAuthorizations: IReturnAuthorization[];
}

export function useQuery(filter?: QueryReturnAuthorizationsFilterInput) {
  const defaultFilterArgs = React.useMemo(
    (): QueryReturnAuthorizationsFilterInput => __normalizeFilter(filter),
    [filter]
  );

  const initQuerySort = React.useMemo(
    () => {
      // Return Requested is pending approval — sort by request date.
      // All other queue tabs are post-approval — sort by approval date.
      const isPendingApprovalOnly = _.isEqual(
        defaultFilterArgs.authorizationApprovalStatus,
        ['pending']
      );

      return {
        columnId: (isPendingApprovalOnly ? 'requestDate' : 'approvalDate') as types.IColumnId,
        direction: 'descending' as const
      };
    },
    [defaultFilterArgs.authorizationApprovalStatus]
  );

  return useListQuery({
    useLazyQuery: __useLazyQueryWithDefaultSort,
    gqlResultData2GqlPageInfo: gql => gql?.returnAuthorizations.pageInfo,
    clientColumn2GqlSortField: {
      requestDate: 'returnDate',
      approvalDate: 'approvalDate',
      orderDate: 'orderDate',
      orderNumber: 'orderNumber',
      orderAmount: 'orderAmount',
      storeName: 'storeName',
      customerEmail: 'customerEmail',
      customerFullName: 'customerFullName',
      customerPhoneNumber: 'customerPhoneNumber',
      returnNumber: 'returnNumber'
    },
    defaultFilterArgs,
    initQuerySort,
    genFilter2GqlFilter: __genFilter2GqlFilter,
    gqlResultData2TupleData: (gql): IData => ({
      returnAuthorizations: _.map(gql.data?.returnAuthorizations.nodes, (ra): IReturnAuthorization => ({
        id: ra.id,
        returnDate: DateTime.fromISO(ra.returnDate),
        itemsToBeReturnedCount: _.sumBy(ra.lineItems, li => li.quantity),
        itemsFulfilledCount: _.sumBy(ra.order.itemFulfillment?.lineItems ?? [], li => li.quantity),
        itemsReceivedCount: ra.itemReceipt?.lineItems.length ?? 0,
        returnNumber: ra.returnNumber,
        shipment: ra.shipment ? {
          id: ra.shipment.id,
          labelUrl: ra.shipment.labelUrl,
          status: (ra.shipment.deliveredScanEvent ? 'delivered' : (ra.shipment.shippedScanEvent ? 'inTransit' : 'awaitingShipment'))
        } : null,
        order: {
          id: ra.order.id,
          externalId: ra.order.rawSourceOrder?.externalId ?? '',
          urlToExternalOrder: ra.order.rawSourceOrder?.url ?? '',
          orderDate: DateTime.fromISO(ra.order.orderDate),
          orderNumber: ra.order.orderNumber,
          totalAmountCents: new Big(ra.order.totalCents),
          netSuiteWriteStatus: NetSuiteRecordChip.genWriteStatus({
            netSuiteRecord: ra.order.netSuiteRecord,
            writeRequest: ra.order.netSuiteWriteRequest
          })
        },
        customer: {
          id: ra.customer.id,
          fullName: ra.customer.firstName + ' ' + ra.customer.lastName,
          phoneNumber: ra.customer.phoneNumber,
          email: ra.customer.email
        },
        store: {
          id: ra.store.id,
          name: ra.store.name
        },
        approval: ra.approval ? {
          createdDate: DateTime.fromISO(ra.approval.metaCreated),
          approvalStatus: ra.approval.approvalType.name
        } : null,
        acceptance: ra.acceptance ? {
          createdDate: DateTime.fromISO(ra.acceptance.metaCreated),
          approvalStatus: ra.acceptance.approvalType.name
        } : null,
        returnAuthorizationNetSuiteWriteStatus: NetSuiteRecordChip.genWriteStatus({
          netSuiteRecord: ra.netSuiteRecord,
          writeRequest: ra.netSuiteWriteRequest
        })
      }))
    })
  });
}



function __normalizeFilter(filter?: QueryReturnAuthorizationsFilterInput): QueryReturnAuthorizationsFilterInput {
  return {
    ...filter,
    // Empty string is not a valid store UUID; treat as "all stores".
    storeId: filter?.storeId || undefined
  };
}

function __genFilter2GqlFilter(defaultArgs: QueryReturnAuthorizationsFilterInput) {
  return (clientFilter?: QueryReturnAuthorizationsFilterInput): QueryReturnAuthorizationsFilterInput => (
    __normalizeFilter({
      ...defaultArgs,
      ...(clientFilter ?? {})
    })
  );
}

/**
 * Adapts the Apollo lazy query to listQueryUtil and always sorts by returnDate.
 * Tab/store filters are applied via genFilter2GqlFilter / defaultFilterArgs.
 */
function __useLazyQueryWithDefaultSort() {
  const [fnQuery, tuple] = errorUtil.useWrapLazyQueryOrMutationTuple(
    useReturnAuthorizationsActivity_ReturnAuthorizationTableLazyQuery({
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache'
    })
  );

  const fnWrappedQuery = React.useCallback(
    (params: {
      variables: Pick<ReturnAuthorizationsActivity_ReturnAuthorizationTableQueryVariables, 'page' | 'filter' | 'sort'>;
    }) => {
      fnQuery({
        variables: {
          page: params.variables.page,
          filter: params.variables.filter,
          sort: params.variables.sort ?? {
            direction: 'descending',
            field: 'returnDate'
          }
        }
      });
    },
    [fnQuery]
  );

  return [
    fnWrappedQuery,
    tuple
  ] as const;
}



export function useGenerateReturnLabel(storeId?: string) {
  const [fnMutate, tuple] = useGenerateReturnLabelMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: ReturnAuthorizationsActivity_TabBarDocument,
        variables: {
          storeId: storeId || undefined
        }
      }
    ]
  });

  const fnWrappedMutate = React.useCallback(
    async (returnAuthorizationId: string) => {
      await fnMutate({
        variables: {
          input: {
            returnAuthorizationId
          }
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
